import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGetDashboardOverview, apiGetAllEmployees } from '../../api';

export default function AdminOverview() {
    const navigate = useNavigate();

    // Khởi tạo các state lưu trữ dữ liệu từ API
    const [isLoading, setIsLoading] = useState(true);
    const [empStats, setEmpStats] = useState({ total: 0, newThisMonth: 0, resignedThisMonth: 0 });
    const [attendance, setAttendance] = useState({ present: 0, dayShift: 0, nightShift: 0, presentRate: 0 });
    const [payroll, setPayroll] = useState({ total: 0, yymm: '' });
    
    const [chartData, setChartData] = useState([]);
    const [newEmployees, setNewEmployees] = useState([]);
    const [resignedEmployees, setResignedEmployees] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // 1. Lấy dữ liệu tổng quan (thống kê, chấm công, quỹ lương, biểu đồ)
                const overviewData = await apiGetDashboardOverview();
                if (overviewData) {
                    setEmpStats(overviewData.empStats);
                    setAttendance(overviewData.attendance);
                    setPayroll(overviewData.payroll);
                    setChartData(overviewData.weeklyChart || []);
                }

                // 2. Lấy danh sách nhân viên để lọc ra người mới và người vừa nghỉ
                const employees = await apiGetAllEmployees();
                if (employees) {
                    const currentMonth = new Date().getMonth() + 1;
                    const currentYear = new Date().getFullYear();

                    // Lọc nhân viên mới trong tháng
                    const newEmps = employees.filter(emp => {
                        if (!emp.INDAT) return false;
                        const inDate = new Date(emp.INDAT);
                        return inDate.getMonth() + 1 === currentMonth && inDate.getFullYear() === currentYear;
                    });

                    // Lọc nhân viên nghỉ việc trong tháng
                    const resEmps = employees.filter(emp => {
                        if (emp.STATUS !== 'Q' && !emp.OUTDAT) return false;
                        if (!emp.OUTDAT) return false;
                        const outDate = new Date(emp.OUTDAT);
                        return outDate.getMonth() + 1 === currentMonth && outDate.getFullYear() === currentYear;
                    });

                    setNewEmployees(newEmps.slice(0, 5));
                    setResignedEmployees(resEmps.slice(0, 5));
                }

            } catch (error) {
                console.error("Lỗi khi tải dữ liệu Dashboard:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Tính toán mốc cao nhất cho biểu đồ
    const maxTotalInWeek = Math.max(...chartData.map(d => d.total), 1);

    if (isLoading) {
        return (
            <div className="flex h-[75vh] w-full items-center justify-center">
                <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-7">
            
            {/* THAY ĐỔI: Chuyển sang 4 cột (grid-cols-4) trên màn hình lớn để chứa đủ 4 ô */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                
                {/* Ô 1. Tổng nhân sự */}
                <article className="bg-white card-shadow metric-card reveal rounded-3xl border-l-[5px] border-emerald-500 p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Tổng công nhân</p>
                            <p className="text-4xl font-black text-[#102a43] tracking-tight mt-2">{empStats.total}</p>
                        </div>
                        <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                        </div>
                    </div>
                    <p className="mt-4 text-xs font-semibold text-emerald-600 bg-emerald-50 w-fit px-2 py-1 rounded-md">
                        +{empStats.newThisMonth} công nhân vào tháng này
                    </p>
                </article>

                {/* Ô 3. Đã vào ca hôm nay */}
                <article className="bg-white card-shadow metric-card reveal reveal-2 rounded-3xl border-l-[5px] border-sky-500 p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Đã vào ca</p>
                            <p className="mt-2 text-4xl font-black text-[#102a43] tracking-tight">{attendance.present}</p>
                        </div>
                        <div className="rounded-2xl bg-sky-50 p-3 text-sky-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        {/* HIỂN THỊ CA NGÀY / CA ĐÊM */}
                        <span className="text-[11px] font-bold text-sky-700 bg-sky-100 px-2 py-1 rounded-md flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-sky-500"></span> 
                            Ca ngày: {attendance.dayShift || 0}
                        </span>
                        <span className="text-[11px] font-bold text-[#102a43] bg-slate-200 px-2 py-1 rounded-md flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-[#102a43]"></span> 
                            Ca đêm: {attendance.nightShift || 0}
                        </span>
                        <span className="ml-auto text-xs font-semibold text-slate-400">{attendance.presentRate}%</span>
                    </div>
                </article>
                {/* Ô 2. Nhân sự nghỉ việc (MỚI THÊM) */}
                <article className="bg-white card-shadow metric-card reveal reveal-1 rounded-3xl border-l-[5px] border-rose-500 p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Nghỉ việc tháng này</p>
                            <p className="text-4xl font-black text-[#102a43] tracking-tight mt-2">{empStats.resignedThisMonth}</p>
                        </div>
                        <div className="rounded-2xl bg-rose-50 p-3 text-rose-600">
                            {/* Icon Nhân viên rời đi (User Minus) */}
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"></path></svg>
                        </div>
                    </div>
                </article>

                {/* Ô 4. Quỹ lương dự kiến */}
                <article className="bg-white card-shadow metric-card reveal reveal-3 rounded-3xl border-l-[5px] border-violet-500 p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Quỹ lương dự kiến</p>
                            <p className="text-2xl font-black text-violet-700 tracking-tight mt-2">
                                {payroll.total > 0 
                                    ? new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(payroll.total) 
                                    : 'Đang tính...'}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-violet-50 p-3 text-violet-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </div>
                    </div>
                    <p className="mt-4 text-[11px] font-semibold text-slate-500 bg-slate-100 w-fit px-2 py-1 rounded-md">
                        {payroll.yymm ? `Kỳ lương hiện tại: ${payroll.yymm.substring(4,6)}/${payroll.yymm.substring(0,4)}` : 'Kỳ lương hiện tại: Đang cập nhật'}
                    </p>
                </article>
            </div>

            <div className="grid grid-cols-1 gap-7 xl:grid-cols-5">
                {/* Panel Biểu đồ Thời gian thực */}
                <section className="bg-white card-shadow reveal rounded-3xl p-6 xl:col-span-3">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="rounded-xl bg-sky-100 p-2 text-sky-700">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                                </div>
                                <h2 className="font-bold text-[#102a43] text-lg">Nhân sự đi làm thực tế</h2>
                            </div>
                            <p className="mt-1 text-xs text-slate-500">Số lượng nhân sự có mặt theo ca Ngày / Đêm trong tuần.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 uppercase tracking-wider"><span className="w-2 h-2 rounded bg-sky-400"></span> Ca Ngày</span>
                            <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 uppercase tracking-wider"><span className="w-2 h-2 rounded bg-[#102a43]"></span> Ca Đêm</span>
                        </div>
                    </div>
                    
                    {/* Vẽ Biểu đồ Cột Xếp Chồng */}
                    <div className="mt-8 flex h-48 items-end justify-between gap-2 border-b border-slate-200 px-2 pb-1 relative">
                        {chartData.length > 0 ? chartData.map((d, idx) => {
                            const dayHeight = (d.dayShift / maxTotalInWeek) * 100;
                            const nightHeight = (d.nightShift / maxTotalInWeek) * 100;
                            
                            return (
                                <div key={idx} className="group relative flex h-full flex-1 flex-col justify-end items-center px-1">
                                    {/* Tooltip khi Hover */}
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#102a43] text-white text-[10px] font-bold py-1 px-2 rounded-lg whitespace-nowrap z-10 pointer-events-none shadow-lg">
                                        Tổng: {d.total} (Ngày: {d.dayShift} | Đêm: {d.nightShift})
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#102a43]"></div>
                                    </div>
                                    
                                    <div 
                                        className="w-full bg-[#102a43] rounded-t-lg transition-all duration-700 ease-in-out"
                                        style={{ height: `${nightHeight}%` }}
                                    ></div>
                                    <div 
                                        className={`w-full bg-sky-400 transition-all duration-700 ease-in-out ${d.nightShift === 0 ? 'rounded-t-lg' : ''} ${d.total === 0 ? 'rounded-lg bg-slate-100 min-h-[4px]' : 'rounded-b-sm'}`}
                                        style={{ height: `${dayHeight}%` }}
                                    ></div>
                                </div>
                            );
                        }) : (
                            <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-slate-400">
                                Đang tải biểu đồ...
                            </div>
                        )}
                    </div>
                    <div className="mt-3 flex justify-between px-2 text-center text-xs font-bold text-slate-400 uppercase">
                        {chartData.map((d, i) => (
                            <span key={i} className="flex-1 block">{d.day}</span>
                        ))}
                    </div>
                </section>

                {/* Panel Tác vụ Nhanh */}
                <section className="bg-white card-shadow reveal rounded-3xl p-6 xl:col-span-2">
                    <div className="flex items-center gap-2">
                        <div className="rounded-xl bg-lime-100 p-2 text-emerald-800">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                        </div>
                        <h2 className="font-bold text-[#102a43] text-lg">Tác vụ nhanh</h2>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Đi đến các hạng mục cần ưu tiên xử lý.</p>
                    
                    <div className="mt-6 grid grid-cols-2 gap-4">
                        <button 
                            onClick={() => navigate('/leave?tab=manage')} 
                            className="flex flex-col items-start bg-indigo-50 hover:bg-indigo-100 hover:shadow-md transition-all rounded-2xl p-4 text-indigo-700 group"
                        >
                            <svg className="w-6 h-6 mb-3 opacity-70 group-hover:opacity-100 transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                            <span className="font-bold text-sm">Quản lý Phép</span>
                        </button>
                        
                        <button 
                            onClick={() => navigate('/payroll?tab=approve')} 
                            className="flex flex-col items-start bg-fuchsia-50 hover:bg-fuchsia-100 hover:shadow-md transition-all rounded-2xl p-4 text-fuchsia-700 group"
                        >
                            <svg className="w-6 h-6 mb-3 opacity-70 group-hover:opacity-100 transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                            <span className="font-bold text-sm">Duyệt Lương</span>
                        </button>

                        <button 
                            onClick={() => navigate('/employee?tab=add')} 
                            className="col-span-2 flex items-center justify-between bg-slate-50 hover:bg-slate-100 hover:shadow-md transition-all rounded-2xl p-4 border border-slate-100 group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-white p-2 rounded-full shadow-sm text-slate-700">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
                                </div>
                                <span className="font-bold text-[#102a43] text-sm">Thêm nhân viên mới</span>
                            </div>
                            <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                        </button>
                    </div>
                </section>
            </div>

            <div className="grid grid-cols-1 gap-7 xl:grid-cols-2">
                
                {/* Panel Danh sách nghỉ việc */}
                <section className="bg-white card-shadow reveal rounded-3xl p-6">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                        <div className="flex items-center gap-2">
                            <div className="rounded-xl bg-rose-100 p-2 text-rose-700">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"></path></svg>
                            </div>
                            <h2 className="font-bold text-[#102a43] text-lg">Nhân sự nghỉ việc</h2>
                        </div>
                        <span className="text-xs font-bold bg-rose-50 text-rose-600 px-2.5 py-1 rounded-full">
                            Tháng này
                        </span>
                    </div>
                    <div className="space-y-3">
                        {resignedEmployees.length > 0 ? resignedEmployees.map(emp => (
                            <div key={emp.EMPID} className="flex items-center justify-between p-3 rounded-2xl border border-slate-50 hover:bg-slate-50 transition">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 font-bold flex items-center justify-center text-xs">
                                        {emp.EMPNAM_VN ? emp.EMPNAM_VN.charAt(0) : 'X'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-[#102a43]">{emp.EMPNAM_VN}</p>
                                        <p className="text-[11px] text-slate-500 font-medium">Mã NV: {emp.EMPID}</p>
                                    </div>
                                </div>
                                <span className="text-[11px] font-semibold text-rose-500 bg-rose-50 px-2 py-1 rounded">
                                    Nghỉ: {emp.OUTDAT ? new Date(emp.OUTDAT).toLocaleDateString('vi-VN') : '---'}
                                </span>
                            </div>
                        )) : (
                            <div className="text-center py-6">
                                <p className="text-sm text-slate-500 font-medium">Chưa có nhân sự nghỉ việc trong tháng.</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* Panel Danh sách nhân viên mới */}
                <section className="bg-white card-shadow reveal rounded-3xl p-6">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                        <div className="flex items-center gap-2">
                            <div className="rounded-xl bg-violet-100 p-2 text-violet-700">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
                            </div>
                            <h2 className="font-bold text-[#102a43] text-lg">Công nhân mới</h2>
                        </div>
                        <button onClick={() => navigate('/employee?tab=list')} className="text-xs font-bold text-violet-600 hover:text-violet-700 hover:underline">
                            Xem tất cả &rarr;
                        </button>
                    </div>
                    <div className="space-y-3">
                        {newEmployees.length > 0 ? newEmployees.map(emp => (
                            <div key={emp.EMPID} className="flex items-center justify-between p-3 rounded-2xl border border-slate-50 hover:bg-slate-50 transition">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 font-bold flex items-center justify-center text-xs">
                                        {emp.EMPNAM_VN ? emp.EMPNAM_VN.charAt(0) : 'X'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-[#102a43]">{emp.EMPNAM_VN}</p>
                                        <p className="text-[11px] text-slate-500 font-medium">Mã NV: {emp.EMPID}</p>
                                    </div>
                                </div>
                                <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                                    Vào: {emp.INDAT ? new Date(emp.INDAT).toLocaleDateString('vi-VN') : '---'}
                                </span>
                            </div>
                        )) : (
                            <div className="text-center py-6">
                                <p className="text-sm text-slate-500 font-medium">Chưa có nhân sự mới trong tháng.</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
            
            <style>{`
                .card-shadow { box-shadow: 0 12px 28px rgba(16,42,67,.04); }
                .metric-card { transition: transform .2s ease, box-shadow .2s ease; }
                .metric-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px rgba(16,42,67,.08); }
                @keyframes reveal { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .reveal { animation: reveal .5s both; }
                .reveal-1 { animation-delay: .1s; }
                .reveal-2 { animation-delay: .2s; }
                .reveal-3 { animation-delay: .3s; }
            `}</style>
        </div>
    );
}